import { Component, inject, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { AsyncPipe, CommonModule } from '@angular/common'
import { catchError, finalize, map, Observable, of, share, tap } from 'rxjs'

// Services
import { AffairsService } from '../../services/affairs.service'
import { ProductsService } from '../../../products/services/products.service'
import { MailsService } from '../../../mails/services/mails.service'

// PrimeNG Components
import { CardModule } from 'primeng/card'
import { ButtonModule } from 'primeng/button'
import { ProgressSpinnerModule } from 'primeng/progressspinner'

// App Components
import { AffairInformationsComponent } from './affair-informations/affair-informations.component'
import { AffairProductsComponent } from './affair-products/affair-products.component'
import { AffairMailsComponent } from './affair-mails/affair-mails.component'
import { AffairInterventionsComponent } from './affair-interventions/affair-interventions.component'
import { InterventionsService } from '../../../interventions/services/interventions.service'
import { AffairPoolOptimizationComponent } from '../affair-pool-optimization/affair-pool-optimization.component'

interface TabItem {
  id: number
  label: string
  icon: string
}

@Component({
  selector: 'app-affair',
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    CardModule,
    ButtonModule,
    ProgressSpinnerModule,
    AffairInformationsComponent,
    AffairProductsComponent,
    AffairMailsComponent,
    AffairInterventionsComponent,
    AffairPoolOptimizationComponent,
  ],
  templateUrl: './affair.component.html',
  styleUrl: './affair.component.scss',
})
export class AffairComponent implements OnInit {
  private route = inject(ActivatedRoute)
  private affairsService = inject(AffairsService)
  private productsService = inject(ProductsService)
  private mailsService = inject(MailsService)

  private mlRef = this.route.snapshot.params['mlRef']

  public activeTab = 0
  public affairLoading = true
  public productsLoading = true
  public mailsLoading = true
  public dataLoadError = false

  public tabs: TabItem[] = [
    { id: 0, label: 'Informations', icon: 'pi pi-info' },
    { id: 1, label: 'Produits', icon: 'pi pi-shopping-cart' },
    { id: 2, label: 'Interventions', icon: 'pi pi-calendar' },
    { id: 3, label: 'Mails', icon: 'pi pi-envelope' },
  ]

  public affair$!: Observable<any>
  public products$!: Observable<any>
  public mails$!: Observable<any>
  public interventions$!: Observable<any>

  ngOnInit(): void {
    this.initializeData()
  }

  public initializeData(): void {
    this.affairLoading = true
    this.productsLoading = true
    this.mailsLoading = true
    this.dataLoadError = false

    // Load affairs data and force subscription
    this.affair$ = this.affairsService.getAffairById(this.mlRef).pipe(
      tap(() => console.log('Affair data loaded')),
      map((affair) => ({
        ...affair,
        srcErp: Object.entries(affair.srcErp).map(([society, value]) => ({
          society,
          value,
        })),
      })),
      catchError((err) => {
        console.error('Error loading affair data:', err)
        this.dataLoadError = true
        return of(null) // Return null on error to signal in the template
      }),
      finalize(() => {
        this.affairLoading = false
      }),
      share() // Share the subscription across multiple subscribers
    )

    // Load products data
    this.products$ = this.productsService.getProductsByMlRef(this.mlRef).pipe(
      catchError(() => of([])),
      finalize(() => (this.productsLoading = false)),
      share()
    )

    // Load mails data
    this.mails$ = this.mailsService.getMailsByMlRef(this.mlRef).pipe(
      catchError(() => of([])),
      finalize(() => (this.mailsLoading = false)),
      share()
    )

    // Load interventions data
    this.interventions$ = this.affairsService.getInterventions(this.mlRef).pipe(
      catchError(() => of([])),
      share()
    )

    // Force immediate subscription to the affair observable
    const subscription = this.affair$.subscribe({
      next: (data) => {
        if (data === null) {
          this.dataLoadError = true
        }
      },
      error: () => {
        this.dataLoadError = true
        this.affairLoading = false
      },
      complete: () => {
        subscription?.unsubscribe()
      },
    })
  }

  public setActiveTab(tabId: number): void {
    this.activeTab = tabId
  }

  public isActiveTab(tabId: number): boolean {
    return this.activeTab === tabId
  }

  navigateToEros(product: any) {}
}
